/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import BrandedSpinner from "@/components/BrandedSpinner";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

type SubscriptionState = {
  subscribed: boolean;
  status: string | null;
  subscriptionEnd: string | null;
  trialEnd: string | null;
  onTrial: boolean;
  storeCount: number;
  extraStores: number;
  monthlyTotal: number;
  loading: boolean;
  checkFailed: boolean;
  fromCache: boolean;
  stale: boolean;
};

// ── Subscription localStorage cache ───────────────────────────────────────────
// Persists subscription state for 24 hours so the edge function is only called
// once per session (or once per day), never mid-session.

const SUB_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

type SubCacheEntry = {
  state: Omit<SubscriptionState, "loading" | "checkFailed">;
  checkedAt: number;
};

function subCacheKey(userId: string) {
  return `vrg_sub_cache_${userId}`;
}

function readSubCache(userId: string): SubCacheEntry | null {
  try {
    const raw = localStorage.getItem(subCacheKey(userId));
    if (!raw) return null;
    const parsed: SubCacheEntry = JSON.parse(raw);
    if (Date.now() - parsed.checkedAt > SUB_CACHE_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSubCache(userId: string, state: SubscriptionState) {
  try {
    const entry: SubCacheEntry = {
      state: {
        subscribed: state.subscribed,
        status: state.status,
        subscriptionEnd: state.subscriptionEnd,
        trialEnd: state.trialEnd,
        onTrial: state.onTrial,
        storeCount: state.storeCount,
        extraStores: state.extraStores,
        monthlyTotal: state.monthlyTotal,
        fromCache: state.fromCache,
        stale: state.stale,
      },
      checkedAt: Date.now(),
    };
    localStorage.setItem(subCacheKey(userId), JSON.stringify(entry));
  } catch {
    // localStorage unavailable or full — silently skip
  }
}

function clearSubCache(userId: string) {
  try {
    localStorage.removeItem(subCacheKey(userId));
  } catch { /* localStorage unavailable */ }
}

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  subscription: SubscriptionState;
  refreshSubscription: () => Promise<void>;
  _signingOut: React.MutableRefObject<boolean>;
};

const defaultSub: SubscriptionState = {
  subscribed: false,
  status: null,
  subscriptionEnd: null,
  trialEnd: null,
  onTrial: false,
  storeCount: 0,
  extraStores: 0,
  monthlyTotal: 0,
  loading: true,
  checkFailed: false,
  fromCache: false,
  stale: false,
};

const _defaultSigningOut = { current: false };

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
  subscription: defaultSub,
  refreshSubscription: async () => {},
  _signingOut: _defaultSigningOut,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionState>(defaultSub);

  const refreshSubscription = useCallback(async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        setSubscription({ ...defaultSub, loading: false });
        return;
      }
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      const newState: SubscriptionState = {
        subscribed: data.subscribed ?? false,
        status: data.status ?? null,
        subscriptionEnd: data.subscription_end ?? null,
        trialEnd: data.trial_end ?? null,
        onTrial: data.on_trial ?? false,
        storeCount: data.store_count ?? 0,
        extraStores: data.extra_stores ?? 0,
        monthlyTotal: data.monthly_total ?? 0,
        loading: false,
        checkFailed: false,
        fromCache: data.from_cache ?? false,
        stale: data.stale ?? false,
      };
      writeSubCache(currentSession.user.id, newState);
      setSubscription(newState);
    } catch (e) {
      console.error("Failed to check subscription (background):", e);
      // If we already have valid subscription state (from cache or a prior successful check),
      // keep it silently — a network blip should never interrupt an active session.
      // Only mark checkFailed if we have no state at all (first-ever load, no cache).
      setSubscription(prev => {
        if (!prev.loading && !prev.checkFailed) return prev;
        return { ...defaultSub, loading: false, checkFailed: true };
      });
    }
  }, []);

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
      if (session) {
        // Apply cached subscription immediately — no loading delay for the user
        const cached = readSubCache(session.user.id);
        if (cached) {
          setSubscription({ ...cached.state, loading: false, checkFailed: false });
        }
        // Always fire a background refresh to keep the cache fresh
        setTimeout(() => refreshSubscription().catch(console.error), 0);
      } else {
        setSubscription({ ...defaultSub, loading: false });
      }
    });

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setLoading(false);
        if (session) {
          const cached = readSubCache(session.user.id);
          if (cached) {
            setSubscription({ ...cached.state, loading: false, checkFailed: false });
          }
          refreshSubscription().catch(console.error);
        }
      })
      .catch(console.error);

    return () => authSub.unsubscribe();
  }, [refreshSubscription]);

  useEffect(() => {
    if (!session) return;
    // Re-check once every 24 hours in the background — never mid-session interruptions
    const interval = setInterval(() => {
      refreshSubscription().catch(console.error);
    }, SUB_CACHE_TTL);
    return () => clearInterval(interval);
  }, [session, refreshSubscription]);

  const signingOut = useRef(false);

  const signOut = async () => {
    signingOut.current = true;
    if (session?.user?.id) clearSubCache(session.user.id);
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut, subscription, refreshSubscription, _signingOut: signingOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading, _signingOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || _signingOut.current) return;

    if (!session) {
      const from = location.pathname + location.search;
      const target = from === "/" || from === "/auth"
        ? "/auth"
        : `/auth?redirect=${encodeURIComponent(from)}`;
      navigate(target, { replace: true });
      return;
    }

    // If an owner set a temp password for this user, the user_metadata flag
    // force_password_change=true blocks all dashboard access until they
    // change it. Redirect to /reset-password (where they have a valid session
    // and the form is visible). The flag is cleared inside ResetPassword on
    // successful change.
    const forceChange = session.user.user_metadata?.force_password_change === true;
    if (forceChange && location.pathname !== "/reset-password") {
      navigate("/reset-password", { replace: true });
    }
  }, [session, loading, navigate, _signingOut, location]);

  if (loading) {
    return <BrandedSpinner size="full" />;
  }

  if (!session) return null;

  // Block render while the redirect to /reset-password is in flight, otherwise
  // the user briefly sees the dashboard before the navigate resolves.
  const forceChange = session.user.user_metadata?.force_password_change === true;
  if (forceChange && location.pathname !== "/reset-password") {
    return <BrandedSpinner size="full" />;
  }

  return <>{children}</>;
}

/**
 * Detects ?checkout=success in the URL and forces a subscription refresh.
 * Place inside any route that Stripe redirects back to.
 */
export function useCheckoutReturn() {
  const { refreshSubscription } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      // Remove the query param so it doesn't re-trigger
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
      // Force refresh — the webhook may have already invalidated cache
      const refresh = async () => {
        // Small delay to let webhook process
        await new Promise((r) => setTimeout(r, 1500));
        await refreshSubscription();
      };
      refresh().catch(console.error);
    }
  }, [searchParams, setSearchParams, refreshSubscription]);
}

export function RequireSubscription({ children }: { children: ReactNode }) {
  const { subscription, session, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [graceExpired, setGraceExpired] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const isSubscriptionPage = location.pathname === "/dashboard/subscription";

  // Give the subscription check 3 seconds before showing paywall
  useEffect(() => {
    const timer = setTimeout(() => setGraceExpired(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Don't show paywall while subscription is still loading or session hasn't settled
  if (subscription.loading || !session || !graceExpired) {
    return <>{children}</>;
  }

  // Edge function failed with no cached state — allow access with a non-blocking notice.
  // This only happens on a brand-new device with no cache AND a network failure at the same time.
  // A network blip during an active session never reaches here (cached state is kept silently).
  if (subscription.checkFailed && !isSubscriptionPage) {
    return (
      <>
        <div className="mx-3 mb-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-warning-foreground flex items-center justify-between gap-4">
          <span>Having trouble verifying your subscription — you can keep working while we retry.</span>
          <button
            disabled={retrying}
            onClick={async () => {
              setRetrying(true);
              await refreshSubscription();
              setRetrying(false);
            }}
            className="shrink-0 rounded-md bg-warning/20 px-3 py-1 text-xs font-medium hover:bg-warning/30 transition-colors disabled:opacity-60"
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        </div>
        {children}
      </>
    );
  }

  // Stale cache — show soft warning but allow access
  if (subscription.subscribed && subscription.stale && !isSubscriptionPage) {
    return (
      <>
        <div className="mx-3 mb-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-warning-foreground">
          Subscription check delayed — using cached status. This will update automatically.
        </div>
        {children}
      </>
    );
  }

  if (!subscription.subscribed && !isSubscriptionPage) {
    return (
      <div className="relative">
        <div className="pointer-events-none select-none opacity-30 blur-[2px]">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm z-10">
          <div className="mx-4 max-w-md rounded-xl border bg-card p-8 text-center shadow-lg space-y-4">
            <h2 className="font-display text-xl font-bold">Subscription Required</h2>
            <p className="text-sm text-muted-foreground">
              You need an active subscription to access this feature. Subscribe now to unlock everything.
            </p>
            <button
              onClick={() => navigate("/dashboard/subscription")}
              className="inline-flex items-center justify-center rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
